import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, ExternalLink, Paperclip, Image as ImageIcon, Trash2, X, Send, CheckCircle2, HelpCircle, RefreshCw,
  Pencil, Check, AlertTriangle, Zap, Landmark, FileText, Download, Mail, CheckCheck, Link2, RotateCcw,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PaymentSheet from '../components/PaymentSheet';
import BulkPayDialog from '../components/BulkPayDialog';
import usePollWhileActive from '../hooks/usePollWhileActive';
import PetanqueLoader from '../components/PetanqueLoader';
import { useAuth } from '../contexts/AuthProvider';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Dialog } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { moneyExact, fmtDate, CURRENCIES } from '../lib/format';
import { looksLikeIban, LOCAL_ROUTING, LOCAL_COUNTRIES } from '../lib/bank';
import { cn } from '../lib/utils';

const STATUS = {
  pending: { label: 'To pay', variant: 'warning' },
  paid: { label: 'Paid · to finalize', variant: 'info' },
  finalized: { label: 'Finalized', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};

// Render free text with any http(s) URL turned into a clickable link.
const URL_RE = /(https?:\/\/[^\s]+)/g;
function Linkify({ text }) {
  const s = String(text || '');
  if (!s) return null;
  return (
    <>
      {s.split(URL_RE).map((part, i) => (/^https?:\/\//.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
           className="break-all text-primary underline hover:no-underline">{part}</a>
      ) : part))}
    </>
  );
}
// coloured native-select look for the admin status control (echoes the sheet)
const STATUS_SELECT = {
  pending: 'border-amber-300 bg-amber-500/10 text-amber-700',
  paid: 'border-sky-300 bg-sky-500/10 text-sky-700',
  finalized: 'border-emerald-300 bg-emerald-500/10 text-emerald-700',
  cancelled: 'border-border bg-muted text-muted-foreground',
};
const TABS = [
  { key: 'active', label: 'To pay' },
  { key: 'to_finalize', label: 'To finalize' },
  { key: 'archived', label: 'Archived' },
];
const EMPTY = {
  active: 'No refunds to pay — add one with the button above.',
  to_finalize: 'Nothing to finalize — paid refunds land here until the customer has been told.',
  archived: 'Nothing archived yet.',
};

// What Odoo says about the credit note behind a refund - a hint only, the
// status is still moved by hand. 'paid' = refunded or netted against an invoice.
const ODOO_STATE = {
  paid: { label: 'Paid in Odoo', variant: 'success', hint: 'the credit note is settled — worth marking this refund Paid' },
  partial: { label: 'Partially paid in Odoo', variant: 'warning', hint: 'part of the credit note is settled' },
  not_paid: { label: 'Open in Odoo', variant: 'outline', hint: 'the credit note has not been refunded yet' },
  in_payment: { label: 'In payment in Odoo', variant: 'accent', hint: 'a payment is registered but not reconciled' },
  reversed: { label: 'Reversed in Odoo', variant: 'default', hint: 'the credit note was reversed' },
};
function OdooHint({ refund, compact = false }) {
  if (!refund.odoo_checked_at) {
    return compact ? null : <span className="text-muted-foreground">not checked yet</span>;
  }
  if (!refund.odoo_move_id) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title="No customer credit note with this name in Odoo — check the reference">
        <HelpCircle className="h-3.5 w-3.5" /> not found in Odoo
      </span>
    );
  }
  const st = ODOO_STATE[refund.odoo_payment_state] || { label: refund.odoo_payment_state, variant: 'default', hint: '' };
  const differs = refund.odoo_amount != null && Math.abs(Number(refund.odoo_amount) - Number(refund.amount)) > 0.009;
  if (compact) {
    return (
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        <Badge variant={st.variant} title={st.hint}>
          {refund.odoo_paid && <CheckCircle2 className="h-3 w-3" />} {st.label}
        </Badge>
        {differs && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title="The refund amount differs from the credit note in Odoo">
            <AlertTriangle className="h-3 w-3" /> ≠ {moneyExact(refund.odoo_amount, refund.currency)} in Odoo
          </span>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={st.variant}>{refund.odoo_paid && <CheckCircle2 className="h-3 w-3" />} {st.label}</Badge>
        {refund.odoo_amount != null && (
          <span className={cn('tnum', differs ? 'font-medium text-amber-700' : 'text-muted-foreground')}>
            {moneyExact(refund.odoo_amount, refund.currency)}{differs && ' on the credit note ≠ this refund'}
          </span>
        )}
        {refund.odoo_partner && <span className="text-muted-foreground">· {refund.odoo_partner}</span>}
        {refund.odoo_url && (
          <a href={refund.odoo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
            open <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        {st.hint}{refund.odoo_checked_at ? ` · checked ${fmtDate(refund.odoo_checked_at)}` : ''}
      </div>
    </div>
  );
}

// For a non-IBAN account (Australia, UK, US…): the bank's country, its routing
// code(s) and an optional BIC - Airwallex can't pay a local account without them.
function LocalBankFields({ form, set, compact = false }) {
  if (!form.bank_account?.trim() || looksLikeIban(form.bank_account)) return null;
  const routing = LOCAL_ROUTING[form.bank_country] || [];
  const h = compact ? 'h-9' : '';
  return (
    <div className="mt-2 rounded-md border border-dashed border-amber-300 bg-amber-500/5 p-2.5">
      <div className="mb-1.5 text-[11px] text-amber-700">Not an IBAN - which country&apos;s bank, and its routing code?</div>
      <div className="grid grid-cols-2 gap-2">
        <Select value={form.bank_country || ''} onChange={set('bank_country')} className={cn('w-full', h)}>
          <option value="">Country…</option>
          {LOCAL_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </Select>
        <Input value={form.bank_bic || ''} onChange={set('bank_bic')} placeholder="BIC / SWIFT (optional)" className={cn('font-mono', h)} />
        {routing.map((r, i) => (
          <Input key={r.label} value={(i === 0 ? form.bank_routing_value : form.bank_routing_value2) || ''}
                 onChange={set(i === 0 ? 'bank_routing_value' : 'bank_routing_value2')}
                 placeholder={`${r.label} · ${r.placeholder}`} className={cn('font-mono', h)} />
        ))}
        {form.bank_country && routing.length === 0 && (
          <div className="col-span-2 text-[11px] text-muted-foreground">No routing code needed for this country - the account number is enough.</div>
        )}
      </div>
    </div>
  );
}

// The bank account: typed IBAN/text and/or a screenshot the employee attached.
function BankAccountCell({ refund }) {
  const openImage = async () => {
    try {
      const blob = await api.refunds.bankImageBlob(refund.id);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch { /* ignore */ }
  };
  return (
    <div className="flex flex-col gap-1">
      {refund.bank_account && (
        <span className="font-mono text-[12px]">
          {refund.bank_account}
          {!looksLikeIban(refund.bank_account) && (refund.bank_country || refund.bank_routing_value) && (
            <span className="text-muted-foreground"> · {refund.bank_country}{refund.bank_routing_value ? ` ${refund.bank_routing_value}` : ''}{refund.bank_routing_value2 ? ` / ${refund.bank_routing_value2}` : ''}</span>
          )}
        </span>
      )}
      {refund.has_bank_image && (
        <button type="button" onClick={(e) => { e.stopPropagation(); openImage(); }}
          className="inline-flex w-fit items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted">
          <ImageIcon className="h-3.5 w-3.5" /> {refund.bank_image_name || 'bank details'}
        </button>
      )}
      {!refund.bank_account && !refund.has_bank_image && <span className="text-muted-foreground">-</span>}
    </div>
  );
}

export default function Refunds() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_app_admin;
  const [params] = useSearchParams();
  // /refunds?tab=to_finalize (from the "refund paid" notification) opens that tab
  const [tab, setTab] = useState(() => (TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'active'));
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, to_finalize: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null);   // row opened in the detail dialog
  const [checking, setChecking] = useState(false);  // Odoo credit-note check in progress
  const [checkedAt, setCheckedAt] = useState(null);
  const [selected2, setSelected2] = useState(() => new Set());   // ticked for "Pay selected" (payers)
  const [bulk, setBulk] = useState(null);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [active, toFinalize, archived] = await Promise.all([
        api.refunds.list('active'), api.refunds.list('to_finalize'), api.refunds.list('archived'),
      ]);
      setCounts({ active: active.length, to_finalize: toFinalize.length, archived: archived.length });
      setRows(tab === 'archived' ? archived : tab === 'to_finalize' ? toFinalize : active);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Ask Odoo whether each pending refund's credit note is settled - once per
  // visit (and on demand); the sync keeps the hint fresh in between.
  const checkOdoo = useCallback(async () => {
    setChecking(true);
    try {
      const d = await api.refunds.checkOdoo();
      setCheckedAt(new Date());
      if (tab === 'active') setRows(d.results || []);
      setSelected((sel) => (sel ? (d.results || []).find((r) => r.id === sel.id) || sel : sel));
    } catch { /* keep the cached hints */ } finally { setChecking(false); }
  }, [tab]);
  useEffect(() => { checkOdoo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (r, status, note = '') => {
    try {
      const updated = await api.refunds.setStatus(r.id, status, note);
      setSelected((sel) => (sel && sel.id === r.id ? updated : sel));
    } finally { load(true); }
  };
  const saveRemark = async (r, remarks) => {
    try { await api.refunds.update(r.id, { remarks }); } finally { load(true); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete the refund for ${r.client}? This can't be undone.`)) return;
    setRows((rs) => rs.filter((x) => x.id !== r.id));
    try { await api.refunds.remove(r.id); } finally { load(true); }
  };

  // delete: creator (while pending) or admin; edit: anyone while pending, admin always
  const canModify = (r) =>
    isAdmin || (r.added_by_id === user?.id && r.status === 'pending');
  // "Pay selected": payers tick pending refunds with a typed IBAN on the To-do tab
  const canBulk = !!user?.can_pay && tab === 'active';
  const payable = (r) => r.status === 'pending' && Number(r.amount) > 0 && !r.in_payment && !!(r.bank_account || '').trim();
  const payableRows = rows.filter(payable);
  const allTicked = payableRows.length > 0 && payableRows.every((r) => selected2.has(r.id));
  const toggleAll = () => setSelected2(allTicked ? new Set() : new Set(payableRows.map((r) => r.id)));
  const toggle = (id) => setSelected2((sel) => { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectedRows = rows.filter((r) => selected2.has(r.id));
  const selectedTotal = Object.entries(selectedRows.reduce((acc, r) => { acc[r.currency] = (acc[r.currency] || 0) + Number(r.amount); return acc; }, {}))
    .map(([c, a]) => moneyExact(a, c)).join(' + ');
  const saveRefund = async (r, data) => {
    const updated = await api.refunds.update(r.id, data);
    setSelected(updated);
    load(true);
    return updated;
  };

  return (
    <AppLayout
      title="Refund Mgmt"
      description="Customer refunds to be paid out by bank deposit — add them here; an admin works the list and marks each paid."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn('inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-[13px] font-medium transition-colors',
                tab === t.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {t.label}
              <span className="rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
        <button onClick={checkOdoo} disabled={checking} title="Re-check the credit notes in Odoo"
          className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[12px] text-muted-foreground hover:bg-muted disabled:opacity-60">
          <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} />
          {checking ? 'Checking Odoo…' : checkedAt ? `Odoo checked ${checkedAt.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}` : 'Check Odoo'}
        </button>
        {canBulk && selected2.size > 0 && (
          <Button variant="secondary" onClick={() => setBulk(selectedRows)} title="Pay the ticked refunds via Airwallex, one after the other">
            <Zap className="h-4 w-4 text-primary" /> Pay {selected2.size} selected{selectedTotal ? ` · ${selectedTotal}` : ''}
          </Button>
        )}
        <Button onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> Add refund
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[1080px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {canBulk && (
                <TableHead className="w-8">
                  <input type="checkbox" checked={allTicked} onChange={toggleAll} disabled={payableRows.length === 0}
                         title="Tick all payable (pending, amount, typed IBAN)" className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" />
                </TableHead>
              )}
              <TableHead>Added</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Bank account</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Note de crédit</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead className="text-right"> </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="h-40 text-center text-muted-foreground">
                {EMPTY[tab]}
              </TableCell></TableRow>
            )}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} className={cn('cursor-pointer hover:bg-muted/30', selected2.has(r.id) && 'bg-primary/[0.04]')} onClick={() => setSelected(r)}>
                {canBulk && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected2.has(r.id)} onChange={() => toggle(r.id)} disabled={!payable(r)}
                           title={payable(r) ? 'Tick to pay in one go' : r.in_payment ? 'Already in payment' : !(r.bank_account || '').trim() ? 'Type the IBAN first' : 'Fill in the amount first'}
                           className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))] disabled:opacity-40" />
                  </TableCell>
                )}
                <TableCell className="whitespace-nowrap">
                  <div className="text-[13px]">{fmtDate(r.added_date)}</div>
                  {r.added_by_name && <div className="text-[11px] text-muted-foreground">by {r.added_by_name}</div>}
                </TableCell>
                <TableCell className="font-medium">{r.client}</TableCell>
                <TableCell><BankAccountCell refund={r} /></TableCell>
                <TableCell className="text-right font-semibold tnum whitespace-nowrap">
                  {moneyExact(r.amount, r.currency)}
                </TableCell>
                <TableCell>
                  {r.credit_note_ref ? (
                    r.credit_note_url ? (
                      <a href={r.credit_note_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        {r.credit_note_ref} <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      </a>
                    ) : <span className="text-[13px]">{r.credit_note_ref}</span>
                  ) : <span className="text-muted-foreground">-</span>}
                  {r.credit_note_ref && r.status === 'pending' && <OdooHint refund={r} compact />}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {r.in_payment ? (
                    <Badge variant="info"><Zap className="h-3 w-3" /> In payment · {r.airwallex_status}</Badge>
                  ) : isAdmin ? (
                    <Select value={r.status} onChange={(e) => changeStatus(r, e.target.value)}
                      className={cn('h-8 w-[130px] text-[12px] font-medium', STATUS_SELECT[r.status])}>
                      <option value="pending">To pay</option>
                      <option value="paid">Paid · to finalize</option>
                      <option value="finalized">Finalized</option>
                      <option value="cancelled">Cancelled</option>
                    </Select>
                  ) : (
                    <Badge variant={STATUS[r.status]?.variant || 'default'}>{STATUS[r.status]?.label || r.status}</Badge>
                  )}
                  {(r.status === 'paid' || r.status === 'finalized') && r.paid_at && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      paid {fmtDate(r.paid_at)}{r.paid_by_name ? ` by ${r.paid_by_name}` : ''}{r.airwallex_short_reference ? ` · ${r.airwallex_short_reference}` : ''}
                    </div>
                  )}
                  {r.status === 'paid' && (
                    <div className="mt-1">
                      {r.customer_notified_at
                        ? <Badge variant="success"><Check className="h-3 w-3" /> customer told</Badge>
                        : <Badge variant="warning">to tell</Badge>}
                    </div>
                  )}
                </TableCell>
                <TableCell className="max-w-[220px]">
                  {r.remarks
                    ? <div className="line-clamp-2 text-[13px] text-muted-foreground"><Linkify text={r.remarks} /></div>
                    : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {canModify(r) && (
                    <button onClick={() => remove(r)} title="Delete"
                      className="text-muted-foreground hover:text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 text-[12px] text-muted-foreground">{rows.length} refund{rows.length === 1 ? '' : 's'}</div>

      <BulkPayDialog kind="refund" open={!!bulk} items={bulk || []} onClose={() => setBulk(null)}
        onDone={() => { setSelected2(new Set()); load(true); }} />

      <AddRefundDialog open={adding} onClose={() => setAdding(false)}
        onCreated={() => { setAdding(false); setTab('active'); load(true); }} />

      <RefundDetailDialog
        refund={selected} isAdmin={isAdmin} currentUserId={user?.id} canPay={!!user?.can_pay}
        onRefresh={async () => { if (!selected) return; try { const r = await api.refunds.checkTransfer(selected.id); setSelected(r); load(true); } catch { /* ignore */ } }}
        onClose={() => setSelected(null)}
        onChangeStatus={(s) => changeStatus(selected, s)}
        onSaveRemarks={(v) => saveRemark(selected, v)}
        onSave={(data) => saveRefund(selected, data)}
        onDelete={() => { remove(selected); setSelected(null); }} />
    </AppLayout>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-start gap-2 py-2">
      <div className="text-[12px] font-medium text-muted-foreground">{label}</div>
      <div className="min-w-0 text-[13px]">{children}</div>
    </div>
  );
}

function RefundDetailDialog({ refund, isAdmin, currentUserId, canPay, onRefresh, onClose, onChangeStatus, onSaveRemarks, onSave, onDelete }) {
  const [status, setStatus] = useState(refund?.status || 'pending');
  const [paySheet, setPaySheet] = useState(false);
  // tell the customer (draft from the server, editable; email or Gorgias)
  const [mail, setMail] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailVia, setMailVia] = useState('email');
  const [sending, setSending] = useState(false);
  const [mailErr, setMailErr] = useState('');
  const openMail = async () => {
    setMailErr(''); setMailOpen(true);
    if (!mail) {
      try {
        const d = await api.refunds.notifyCustomerDraft(refund.id);
        setMail(d); setMailVia(d.gorgias?.enabled && d.gorgias?.ticket_id && !d.gorgias?.error ? 'gorgias' : 'email');
      } catch (e) { setMailErr(e?.response?.data?.detail || 'Could not prepare the message.'); }
    }
  };
  const sendMail = async (finalize) => {
    if (!mail?.to?.trim()) { setMailErr('A customer email address is needed.'); return; }
    setSending(true); setMailErr('');
    try {
      await api.refunds.notifyCustomer(refund.id, { to: mail.to.trim(), subject: mail.subject, body: mail.body, finalize, via: mailVia });
      setMailOpen(false); setMail(null); await onRefresh?.();
    } catch (e) { setMailErr(e?.response?.data?.detail || 'Could not send.'); }
    finally { setSending(false); }
  };
  // an Airwallex transfer in flight: re-read every 10s until final
  usePollWhileActive(!!refund?.in_payment, () => onRefresh?.(), 10000);
  const openProof = async () => {
    try { const blob = await api.refunds.proofBlob(refund.id); window.open(URL.createObjectURL(blob), '_blank', 'noopener'); } catch { /* ignore */ }
  };
  const [remarks, setRemarks] = useState(refund?.remarks || '');
  const [imgUrl, setImgUrl] = useState(null);
  const [savedNote, setSavedNote] = useState('');
  // edit mode for the details (client / amount / bank / credit note)
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const editFileRef = useRef(null);

  useEffect(() => {
    setStatus(refund?.status || 'pending');
    setRemarks(refund?.remarks || '');
    setSavedNote('');
  }, [refund]);
  useEffect(() => { if (!refund) { setEditing(false); setErr(''); setFile(null); } setMail(null); setMailOpen(false); setMailErr(''); }, [refund?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!refund?.has_bank_image) { setImgUrl(null); return undefined; }
    let url; let cancelled = false;
    api.refunds.bankImageBlob(refund.id)
      .then((b) => { if (!cancelled) { url = URL.createObjectURL(b); setImgUrl(url); } })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [refund]);

  if (!refund) return null;
  // edit: anyone while pending, admin always · delete: creator while pending, or admin
  const canEdit = isAdmin || refund.status === 'pending';
  const canModify = isAdmin || (refund.added_by_id === currentUserId && refund.status === 'pending');
  const isImg = (refund.bank_image_type || '').startsWith('image/');
  const startEdit = () => {
    setForm({ client: refund.client || '', amount: refund.amount, currency: refund.currency || 'EUR',
              bank_account: refund.bank_account || '', credit_note_ref: refund.credit_note_ref || '',
              credit_note_url: refund.credit_note_url || '', gorgias_url: refund.gorgias_url || '',
              bank_country: refund.bank_country || '', bank_routing_value: refund.bank_routing_value || '',
              bank_routing_value2: refund.bank_routing_value2 || '', bank_bic: refund.bank_bic || '' });
    setFile(null); setErr(''); setEditing(true);
  };
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const saveEdit = async () => {
    if (!form.client.trim()) { setErr('Client is required.'); return; }
    if (!(Number(form.amount) > 0)) { setErr('Enter an amount greater than 0.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ ...form, client: form.client.trim(), bank_image: file || undefined });
      setEditing(false);
    } catch (e) {
      const d = e?.response?.data;
      setErr(d?.detail || (d && Object.values(d).flat().join(' ')) || 'Could not save.');
    } finally { setSaving(false); }
  };

  const changeStatus = (s) => { setStatus(s); onChangeStatus(s); };
  const saveRemarks = () => {
    if (remarks === (refund.remarks || '')) return;
    onSaveRemarks(remarks);
    setSavedNote('Saved');
  };

  return (
    <Dialog open={!!refund} onClose={onClose} className="max-w-lg">
      <div className="p-5">
        <div className="mb-1 flex items-start justify-between gap-3 pr-6">
          <h2 className="text-lg font-semibold">{refund.client}</h2>
          <div className="flex items-center gap-2">
            {canPay && refund.status === 'pending' && !refund.in_payment && !editing && (
              <Button size="sm" onClick={() => setPaySheet(true)} title="Pay this refund via Airwallex (payment sheet)">
                <Landmark className="h-4 w-4" /> Pay
              </Button>
            )}
            {refund.in_payment && <Badge variant="info"><Zap className="h-3 w-3" /> In payment · {refund.airwallex_status}</Badge>}
            {refund.status === 'paid' && !editing && (
              <>
                <Button size="sm" onClick={openMail} title="Send the customer the payment proof and finalize"><Mail className="h-4 w-4" /> Tell the customer</Button>
                <button type="button" onClick={() => onChangeStatus('finalized')} title="The customer has been told another way"
                        className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground hover:underline">
                  <CheckCheck className="h-3.5 w-3.5" /> Finalize
                </button>
              </>
            )}
            {refund.status === 'finalized' && isAdmin && !editing && (
              <button type="button" onClick={() => onChangeStatus('paid')} title='Back to "to finalize"'
                      className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground hover:underline">
                <RotateCcw className="h-3.5 w-3.5" /> Reopen
              </button>
            )}
            {canEdit && !editing && (
              <button type="button" onClick={startEdit} title="Edit client, amount, bank account, credit note"
                      className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            <Badge variant={STATUS[status]?.variant || 'default'}>{STATUS[status]?.label || status}</Badge>
          </div>
        </div>
        <div className="text-[12px] text-muted-foreground">
          Added {fmtDate(refund.added_date)}{refund.added_by_name ? ` · by ${refund.added_by_name}` : ''}
          {refund.paid_at && ` · paid ${fmtDate(refund.paid_at)}${refund.paid_by_name ? ` by ${refund.paid_by_name}` : ''}`}
          {refund.customer_notified_at && ` · customer told ${refund.customer_notified_via === 'gorgias' ? 'on Gorgias' : 'by email'} ${fmtDate(refund.customer_notified_at)}`}
          {refund.finalized_at && ` · finalized ${fmtDate(refund.finalized_at)}${refund.finalized_by_name ? ` by ${refund.finalized_by_name}` : ''}`}
        </div>
        {mailOpen && (
          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-500/[0.06] p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px] font-medium">
              <Mail className="h-4 w-4 text-sky-600" /> Tell the customer the refund was sent
              {mail && (
                <span className="ml-auto inline-flex rounded-md border border-border bg-card p-0.5 text-[12px] font-medium">
                  {[['email', 'By email'], ['gorgias', 'On the Gorgias ticket']].map(([k, label]) => {
                    const off = k === 'gorgias' && !(mail.gorgias?.enabled && mail.gorgias?.ticket_id);
                    return (
                      <button key={k} type="button" disabled={off} onClick={() => setMailVia(k)}
                              title={off ? (mail.gorgias?.enabled ? 'Add the Gorgias ticket URL to the refund first (Edit)' : 'Gorgias is not connected') : ''}
                              className={cn('rounded px-2 py-0.5', mailVia === k ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground', off && 'opacity-40')}>
                        {label}
                      </button>
                    );
                  })}
                </span>
              )}
            </div>
            {!mail && !mailErr ? <div className="py-2"><PetanqueLoader size={20} /></div> : mail && (
              <div className="space-y-2">
                {mailVia === 'gorgias' && (
                  <div className="text-[12px] text-muted-foreground">
                    Reply on <a href={mail.gorgias.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">ticket #{mail.gorgias.ticket_id}</a>
                    {mail.gorgias.subject && <> · &ldquo;{mail.gorgias.subject}&rdquo;</>}
                    {mail.gorgias.mailbox && <> · sent from {mail.gorgias.mailbox}</>}
                    {mail.gorgias.agent && <> as {mail.gorgias.agent}</>}
                    {mail.gorgias.error && <span className="text-rose-600"> · {mail.gorgias.error}</span>}
                  </div>
                )}
                <div className="grid grid-cols-[70px_1fr] items-center gap-2 text-[12px]">
                  <span className="text-muted-foreground">To</span>
                  <Input value={mail.to} onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))} placeholder="customer@example.com" className={cn('h-8 text-[12px]', !mail.to && 'border-amber-400')} />
                  <span className="text-muted-foreground">Subject</span>
                  <Input value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} className="h-8 text-[12px]" />
                </div>
                <textarea value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} rows={6}
                          className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary" />
                <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
                  {mail.attachment
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><FileText className="h-3.5 w-3.5" /> {mail.attachment} attached</span>
                    : <span>no payment proof on this refund{refund.airwallex_transfer_id ? ' yet - fetch it below first' : ' (manual transfer)'}</span>}
                  {mailVia === 'email' && mail.reply_to && <span>· replies go to {mail.reply_to}, you&apos;re in cc</span>}
                </div>
                {mailErr && <div className="text-[12px] text-rose-600">{mailErr}</div>}
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" onClick={() => sendMail(true)} disabled={sending}>
                    {sending ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Send & finalize
                  </Button>
                  <button type="button" onClick={() => sendMail(false)} disabled={sending} className="text-[12px] text-muted-foreground hover:underline">Send, keep in &quot;to finalize&quot;</button>
                  <button type="button" onClick={() => setMailOpen(false)} className="ml-auto text-[12px] text-muted-foreground hover:underline">Cancel</button>
                </div>
              </div>
            )}
            {mailErr && !mail && <div className="text-[12px] text-rose-600">{mailErr}</div>}
          </div>
        )}

        {editing ? (
          <div className="mt-3 rounded-lg border border-primary/50 bg-muted/30 p-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-medium">Client <span className="text-rose-500">*</span></label>
                <Input value={form.client} onChange={setF('client')} className="h-9" autoFocus />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium">Amount <span className="text-rose-500">*</span></label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={setF('amount')} className="h-9" />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium">Currency</label>
                <Select value={form.currency} onChange={setF('currency')} className="h-9 w-full">
                  {CURRENCIES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                </Select>
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-medium">Bank account (IBAN / number)</label>
                <Input value={form.bank_account} onChange={setF('bank_account')} className="h-9 font-mono" />
                <LocalBankFields form={form} set={setF} compact />
                <div className="mt-1.5 flex items-center gap-2 text-[12px]">
                  <input ref={editFileRef} type="file" hidden accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                         onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  <button type="button" onClick={() => editFileRef.current?.click()}
                    className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 font-medium text-muted-foreground hover:bg-muted">
                    <Paperclip className="h-3.5 w-3.5" /> {refund.has_bank_image || file ? 'Replace image' : 'Attach image'}
                  </button>
                  {file && <span className="text-muted-foreground">{file.name}</span>}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium">Note de crédit (ref)</label>
                <Input value={form.credit_note_ref} onChange={setF('credit_note_ref')} placeholder="RINV/2026/4887" className="h-9 font-mono" />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium">Note de crédit (link)</label>
                <Input value={form.credit_note_url} onChange={setF('credit_note_url')} placeholder="https://…" className="h-9" />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-[12px] font-medium">Gorgias ticket</label>
                <Input value={form.gorgias_url} onChange={setF('gorgias_url')} placeholder="Paste the customer's ticket URL - lets you reply on it" className="h-9" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button size="sm" onClick={saveEdit} disabled={saving}>
                {saving ? <PetanqueLoader size={16} /> : <Check className="h-4 w-4" />} Save
              </Button>
              <button onClick={() => setEditing(false)} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>
              {err && <span className="text-[12px] text-rose-600">{err}</span>}
            </div>
          </div>
        ) : (
        <div className="mt-3 rounded-lg border border-border">
          <div className="border-b border-border px-3 py-3 text-center">
            <div className="text-2xl font-semibold tnum">{moneyExact(refund.amount, refund.currency)}</div>
          </div>
          <div className="divide-y divide-border px-3">
            <DetailRow label="Bank account">
              {refund.bank_account
                ? <span className="break-all font-mono text-[12px]">{refund.bank_account}</span>
                : <span className="text-muted-foreground">{refund.has_bank_image ? 'see image below' : '-'}</span>}
              {refund.has_bank_image && (
                <div className="mt-2">
                  {isImg && imgUrl
                    ? <a href={imgUrl} target="_blank" rel="noreferrer">
                        <img src={imgUrl} alt={refund.bank_image_name || 'bank details'}
                             className="max-h-52 w-auto rounded-md border border-border" />
                      </a>
                    : <a href={imgUrl || undefined} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        <ImageIcon className="h-3.5 w-3.5" /> {refund.bank_image_name || 'bank details'}
                      </a>}
                </div>
              )}
            </DetailRow>
            <DetailRow label="Note de crédit">
              {refund.credit_note_ref ? (
                refund.credit_note_url ? (
                  <a href={refund.credit_note_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 break-all text-primary hover:underline">
                    {refund.credit_note_ref} <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                  </a>
                ) : refund.credit_note_ref
              ) : <span className="text-muted-foreground">-</span>}
            </DetailRow>
            {refund.credit_note_ref && (
              <DetailRow label="In Odoo"><OdooHint refund={refund} /></DetailRow>
            )}
            {refund.gorgias_url && (
              <DetailRow label="Gorgias">
                <a href={refund.gorgias_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  <Link2 className="h-3.5 w-3.5" /> customer ticket <ExternalLink className="h-3 w-3" />
                </a>
              </DetailRow>
            )}
            {status === 'paid' && refund.paid_by_name && (
              <DetailRow label="Paid">
                <span className="text-muted-foreground">by {refund.paid_by_name}{refund.paid_at ? ` · ${fmtDate(refund.paid_at)}` : ''}</span>
              </DetailRow>
            )}
            {refund.airwallex_transfer_id && (
              <DetailRow label="Airwallex">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Zap className="h-3.5 w-3.5 text-primary" />
                  <span className="font-mono">{refund.airwallex_short_reference || refund.airwallex_transfer_id}</span>
                  <Badge variant={['FAILED', 'CANCELLED'].includes(refund.airwallex_status) ? 'destructive' : ['SENT', 'PAID'].includes(refund.airwallex_status) ? 'success' : 'info'}>{refund.airwallex_status || '…'}</Badge>
                  {refund.airwallex_source_currency && <span className="text-muted-foreground">· from {refund.airwallex_source_currency}</span>}
                  {refund.in_payment && (
                    <button type="button" onClick={onRefresh} className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground hover:underline">
                      <RefreshCw className="h-3.5 w-3.5" /> refresh
                    </button>
                  )}
                </div>
                {refund.airwallex_external_reference && <div className="mt-0.5 text-[11px] text-muted-foreground">bank ref <span className="font-mono">{refund.airwallex_external_reference}</span></div>}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {refund.has_proof
                    ? <button type="button" onClick={openProof} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px] hover:bg-muted"><FileText className="h-3.5 w-3.5 text-primary" /> <span className="max-w-[240px] truncate">{refund.proof_name || 'payment proof'}</span> <ExternalLink className="h-3 w-3 text-muted-foreground" /></button>
                    : <button type="button" onClick={async () => { try { await api.refunds.confirmationLetter(refund.id); onRefresh?.(); } catch { /* ignore */ } }}
                              className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-muted-foreground hover:bg-muted"><Download className="h-3.5 w-3.5" /> Get the payment proof</button>}
                </div>
              </DetailRow>
            )}
            {refund.payment_log?.length > 0 && (
              <DetailRow label="Log">
                <ol className="space-y-1">
                  {[...refund.payment_log].reverse().map((e, i) => (
                    <li key={i} className="text-[12px]"><span className="font-medium">{e.who}</span> <span className="text-muted-foreground">· {fmtDate(e.at)}</span><div className="text-muted-foreground">{e.note}</div></li>
                  ))}
                </ol>
              </DetailRow>
            )}
          </div>
        </div>
        )}

        <PaymentSheet kind="refund" recordId={refund.id} open={paySheet} onClose={() => setPaySheet(false)}
                      onPaid={async () => { setPaySheet(false); await onRefresh?.(); }} />

        {/* status control (admin) */}
        {isAdmin && (
          <div className="mt-4">
            <label className="mb-1 block text-[12px] font-medium">Status</label>
            <Select value={status} onChange={(e) => changeStatus(e.target.value)}
              className={cn('h-9 w-full sm:w-52 font-medium', STATUS_SELECT[status])}>
              <option value="pending">To pay</option>
              <option value="paid">Paid · to finalize</option>
              <option value="finalized">Finalized (archive)</option>
              <option value="cancelled">Cancelled (archive)</option>
            </Select>
          </div>
        )}

        {/* remarks */}
        <div className="mt-4">
          <label className="mb-1 block text-[12px] font-medium">Remarks</label>
          {canEdit ? (
            <>
              <textarea value={remarks} onChange={(e) => { setRemarks(e.target.value); setSavedNote(''); }}
                onBlur={saveRemarks} rows={3} placeholder="Add a remark… (URLs become clickable)"
                className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary" />
              <div className="mt-1 flex items-center gap-3">
                <Button size="sm" variant="secondary" onClick={saveRemarks} disabled={remarks === (refund.remarks || '')}>Save remark</Button>
                {savedNote && <span className="text-[12px] text-emerald-600">{savedNote}</span>}
              </div>
              {remarks && (
                <div className="mt-2 rounded-md bg-muted/40 px-2.5 py-2 text-[13px] leading-relaxed">
                  <Linkify text={remarks} />
                </div>
              )}
            </>
          ) : (
            <div className="rounded-md bg-muted/40 px-2.5 py-2 text-[13px] leading-relaxed">
              {refund.remarks ? <Linkify text={refund.remarks} /> : <span className="text-muted-foreground">No remarks.</span>}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center gap-3">
          {canModify && (
            <button onClick={onDelete}
              className="inline-flex items-center gap-1 text-[13px] text-rose-600 hover:underline">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
          <button onClick={onClose} className="ml-auto text-[13px] font-medium text-muted-foreground hover:underline">Close</button>
        </div>
      </div>
    </Dialog>
  );
}

function AddRefundDialog({ open, onClose, onCreated }) {
  const empty = {
    client: '', bank_account: '', amount: '', currency: 'EUR',
    credit_note_ref: '', credit_note_url: '', remarks: '', gorgias_url: '',
    bank_country: '', bank_routing_value: '', bank_routing_value2: '', bank_bic: '',
  };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (open) { setForm(empty); setFile(null); setErr(''); } }, [open]);

  const submit = async () => {
    if (!form.client.trim()) { setErr('Client is required.'); return; }
    if (!(Number(form.amount) > 0)) { setErr('Enter an amount greater than 0.'); return; }
    setBusy(true); setErr('');
    try {
      await api.refunds.create({ ...form, client: form.client.trim(), bank_image: file || undefined });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save the refund.');
    } finally { setBusy(false); }
  };

  const field = 'mb-1 block text-[12px] font-medium';

  return (
    <Dialog open={open} onClose={onClose} className="max-w-lg">
      <div className="p-5">
        <h2 className="mb-4 text-lg font-semibold">Add a refund</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={field}>Client <span className="text-rose-500">*</span></label>
            <Input value={form.client} onChange={set('client')} placeholder="e.g. CC & SS SRL &quot;La Cuisine&quot;" />
          </div>
          <div>
            <label className={field}>Amount <span className="text-rose-500">*</span></label>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" />
          </div>
          <div>
            <label className={field}>Currency</label>
            <Select value={form.currency} onChange={set('currency')} className="w-full">
              {CURRENCIES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
            </Select>
          </div>
          <div className="col-span-2">
            <label className={field}>Bank account (IBAN / number)</label>
            <Input value={form.bank_account} onChange={set('bank_account')} placeholder="BE97 7380 5083 0749 - or a local account number" className="font-mono" />
            <LocalBankFields form={form} set={set} />
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <button type="button" onClick={() => fileRef.current?.click()}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-muted-foreground hover:bg-muted">
                <Paperclip className="h-3.5 w-3.5" /> {file ? 'Change image' : 'Attach image instead'}
              </button>
              {file && (
                <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                  {file.name}
                  <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                    className="hover:text-rose-600"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
          </div>
          <div>
            <label className={field}>Note de crédit (ref)</label>
            <Input value={form.credit_note_ref} onChange={set('credit_note_ref')} placeholder="RINV/2026/4887" />
          </div>
          <div>
            <label className={field}>Note de crédit (link)</label>
            <Input value={form.credit_note_url} onChange={set('credit_note_url')} placeholder="https://…" />
          </div>
          <div className="col-span-2">
            <label className={field}>Gorgias ticket</label>
            <Input value={form.gorgias_url} onChange={set('gorgias_url')} placeholder="Paste the customer's ticket URL (optional)" />
          </div>
          <div className="col-span-2">
            <label className={field}>Remarks</label>
            <Input value={form.remarks} onChange={set('remarks')} placeholder="Optional note" />
          </div>
        </div>
        {err && <div className="mt-3 text-[13px] text-rose-600">{err}</div>}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Add refund
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
